import React, { useState, useEffect} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { GLOBAL_IMAGES, DOG_ANIMATIONS, ASSESSMENT, COMPONENTS_LessonCom } from "../../assets/asset";
import Lottie from "lottie-react";
import { PopupFail, PopupPass } from './Popup';
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../api/axiosInstance";

const DemoDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { yearId } = location.state || {};
  const { user } = useSelector((state) => state.user);
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [popup, setPopup] = useState(null);
  const [examResult, setExamResult] = useState(null);
  const [showLottie, setShowLottie] = useState(false);
  const [userName, setUserName] = useState("your");
  
  useEffect(() => {
    setUserName(JSON.parse(localStorage.getItem("user"))?.user_name);
  }, [])

  const questionsQuery = useQuery({
    queryKey: ["assessmentQuestions", yearId],
    queryFn: async () => {
      if (!yearId) {
        navigate("/");
        return null;
      }
      
      const formData = new FormData();
      formData.append('YearId', yearId);
      
      const response = await axiosInstance.post(
        'api/questionassementOfYear',
        formData
      );
      
      if (response.data.status === "Success") {
        return response.data.data;
      } else {
        throw new Error("Failed to load assessment questions");
      }
    },
    enabled: !!yearId,
    onError: (error) => {
      toast.error(error.message || "An error occurred while loading the assessment");
    }
  });

  const handleSubmitAssessment = async (answersOverride) => {
    try {
      const formattedAnswers = {};
      const currentAnswers = answersOverride || { ...answers };
      
      questionsQuery.data.forEach(question => {
        const questionId = question.fields.QuestionId;
        if (currentAnswers[questionId] !== undefined) {
          formattedAnswers[questionId] = currentAnswers[questionId].toString();
        }
      });

      const payload = {
        QuestionAnswear: formattedAnswers,
        AssementYear: parseInt(yearId),
        UserId: user?.UserId || user.pk
      };


      const response = await axiosInstance.post('api/newAnswerassement', payload);

      if (response.data.status === "Success") {
        setExamResult("pass");
        setPopup(true);
      } else {
        setExamResult("fail");
        setPopup(true);
      }
    } catch (error) {
      console.error("Assessment submission error:", error);
      toast.error(error.message || "An error occurred while submitting the assessment");
      setExamResult("fail");
      setPopup(true);
    }
  };

  const handleAnswerSelect = (questionId, answerIndex) => {
    const updatedAnswers = {
      ...answers,
      [questionId]: answerIndex,
    };
  
    setAnswers(updatedAnswers);
  
    setTimeout(() => {
      if (currentQuestionIndex < questionsQuery.data.length - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
      } else {
        setShowLottie(true);
  
        setTimeout(() => {
          setShowLottie(false);
          handleSubmitAssessment(updatedAnswers);
        }, 3000);
      }
    }, 1000);
  };

  const handleRetry = () => {
    setPopup(false);
    setAnswers({});
    setCurrentQuestionIndex(0);
    
    const newYearId = parseInt(yearId) === 5 ? yearId : parseInt(yearId) - 1;
    navigate("/demoDashboard", { state: { yearId: newYearId } });
  };

  const handleContinue = () => {
    setPopup(false);
    navigate("/lessonDashboard", { state: { yearId } });
  };

  const renderLottieScreen = () => {
    return (
      <div className="relative w-full max-w-6xl mx-auto flex items-center justify-center min-h-[80vh]" role="region" aria-label="Assessment question">
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <img 
            src={ASSESSMENT.pole} 
            alt="" 
            className="h-full max-h-[90vh] object-contain" 
          />
        </div>
        
        <div className="relative z-10 w-full max-w-5xl">
          <div className="relative">
            <img  
              src={ASSESSMENT.board || ASSESSMENT.poalWithBoard} 
              alt="Question board" 
              className="w-full h-auto max-h-[85vh] object-contain" 
            />
            
            <div className="absolute inset-0 flex flex-col">
              <div className="flex flex-col justify-center items-start px-8 pt-8 pb-4" style={{height: '30%'}}>
                <h2 className="text-lg md:text-xl lg:text-2xl text-black font-mali" id="question-title">
                We are curating {userName || "your" } <strong>Learning experience based</strong> on the assessment
                </h2>
              </div>
              
              <div className="flex flex-col justify-center items-center px-8 py-4" style={{height: '70%'}}>
                <div className={`w-full flex flex-col items-center`}>
                  <div className={`flex flex-wrap justify-center items-center gap-6 md:gap-8 lg:gap-10 `} role="radiogroup" aria-labelledby="question-title">
                  <Lottie animationData={DOG_ANIMATIONS.dog4} className="h-72 w-72" />
                  </div>
                  <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-black font-mali" id="question-title">
                We are curating {userName || "your"} Learning experience based on the assessment
                </h2>                
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentQuestion = () => {
    if (questionsQuery.isPending) return <div className="text-center">Loading assessment questions...</div>;
    if (questionsQuery.isError) return <div className="text-center text-red-500">{questionsQuery.error.message}</div>;
    if (!questionsQuery.data || !questionsQuery.data.length) return <div className="text-center">No assessment questions available</div>;
  
    const currentQuestion = questionsQuery.data[currentQuestionIndex];
    const questionData = currentQuestion.fields;
    const questionId = questionData.QuestionId;
    const selectedAnswer = answers[questionId];

    return (
      <div className="relative w-full max-w-6xl mx-auto flex items-center justify-center min-h-[80vh]" role="region" aria-label="Assessment question">
        <div className="absolute inset-0 flex items-center justify-center z-0">
          <img 
            src={ASSESSMENT.pole} 
            alt="" 
            className="h-full max-h-[90vh] object-contain" 
          />
        </div>
        
        <div className="relative z-10 w-full max-w-5xl">
          <div className="relative">
            <img  
              src={ASSESSMENT.board || ASSESSMENT.poalWithBoard} 
              alt="Question board" 
              className="w-full h-auto max-h-[85vh] object-contain" 
            />
            
            <div className="absolute inset-0 flex flex-col">
              <div className="flex flex-col justify-center items-start px-8 pt-8 pb-4" style={{height: '30%'}}>
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-black font-mali" id="question-title">
                  {questionData.question}
                </h2>
              </div>
              
              <div className="flex flex-col justify-center items-center px-8 py-4" style={{height: '70%'}}>
                <div className={`w-full flex flex-col ${!questionData.questionimagelist || questionData.questionimagelist.length === 0 ? 'justify-center h-full' : 'justify-between gap-20'} items-center`}>
                  {questionData.questionimagelist && questionData.questionimagelist.length > 0 && (
                    <div className="flex justify-center items-center mb-6 mt-2">
                      <div className="flex flex-row gap-4 justify-center">
                        {questionData.questionimagelist.map((imgSrc, idx) => (
                          <img 
                            key={idx}
                            src={imgSrc.trim().replace(/`/g, '')} 
                            alt={`Question visual ${idx + 1}`} 
                            className="max-h-[40px] md:max-h-[80px] lg:max-h-[80px] object-contain"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className={`flex flex-wrap justify-center items-center gap-6 md:gap-8 lg:gap-10 ${!questionData.questionimagelist || questionData.questionimagelist.length === 0 ? 'my-auto' : 'mb-8'}`} role="radiogroup" aria-labelledby="question-title">
                    {questionData.imageList.map((option, index) => {
                      const trimmedOption = option.trim().replace(/`/g, '');
                      const isImageUrl = trimmedOption.startsWith('http');
                      
                      return (
                        <div 
                          key={index}
                          onClick={() => handleAnswerSelect(questionId, index)} 
                          className={`cursor-pointer transition-all duration-200 ${selectedAnswer === index ? 'transform scale-110 ' : 'hover:scale-105'}`}
                          role="radio"
                          aria-checked={selectedAnswer === index}
                          aria-label={isImageUrl ? `Option ${index + 1}` : trimmedOption}
                          tabIndex={0}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              handleAnswerSelect(questionId, index); 
                            }
                          }}
                        >
                          {isImageUrl ? (
                            <img 
                              src={trimmedOption} 
                              alt={`Option ${index + 1}`} 
                              className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 object-contain" 
                            />
                          ) : (
                            <div className="px-7 py-4 rounded-3xl font-bold bg-white font-mali">
                              {trimmedOption}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (   
    <div 
      className="min-h-screen flex flex-col items-center"
      style={{
        backgroundImage: `url(${ASSESSMENT.bg || GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      role="main"
      aria-label="Year assessment"
    >
      <div className="w-full mx-auto">
        <div className="w-full flex px-20 items-center justify-between p-3 bg-white/40 rounded-lg mb-6">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center justify-center" 
            aria-label="Go back"
          >
            <img
              src={COMPONENTS_LessonCom.back}
              alt=""
              className="w-12 h-12"
            />
          </button>
          
          <div className="flex-1 flex items-center justify-center gap-4 mx-4">
            <div className="font-bold text-xl">Evaluation</div>
            
            <div className="w-64 mx-2">
              <div className="bg-gray-200 h-4 rounded-full">
                <div
                  className="bg-blue-600 h-full transition-all duration-300 rounded-full"
                  style={{
                    width: `${((currentQuestionIndex + 1) / (questionsQuery.data?.length || 1)) * 100}%`,
                  }}
                  role="progressbar"
                  aria-valuenow={currentQuestionIndex + 1}
                  aria-valuemin="1"
                  aria-valuemax={questionsQuery.data?.length || 1}
                ></div>
              </div>
            </div>
          </div>
          
          <div className="text-black text-xl font-bold">
            {currentQuestionIndex + 1}/{questionsQuery.data?.length || 0}
          </div>
        </div>
        
        <div className="container mx-auto">
          {popup && examResult === "pass" && <PopupPass onContinue={handleContinue}/>}
          {popup && examResult === "fail" && <PopupFail onRetry={handleRetry} />}
          
          {showLottie ? (
            renderLottieScreen()
          ) : currentQuestionIndex === (questionsQuery.data?.length || 0) ? (
            renderLottieScreen()
          ) : (
            renderCurrentQuestion()
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoDashboard;